import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { Button, LoaderCircle, UtilityInlineGrid } from 'rhinostyle';
import { useGetAppointmentLinkStatusQuery, useUpdateAppointmentStatusMutation } from '../services/appointmentService';
import { replaceNewLinesToLineBreaks } from '../helpers/TemplateHelpers';

const APPOINTMENT_DATE_MAX_LENGTH = 100;
const LINK_CHECK_FAILED_MESSAGE = 'We were unable to load this appointment link. Please try again or contact the office.';

const AppointmentReminderAck = () => {
  const params = useParams();
  const location = useLocation();
  const [acknowlegement, setAcknowlegement] = React.useState('');
  const [keptAppointment, setKeptAppointment] = React.useState(false);
  const [updateAppointmentStatus, data] = useUpdateAppointmentStatusMutation();

  const { message } = data?.error?.data || {};
  const isCancel = location.pathname.includes('cancel');

  // the reminder link carries the formatted appointment date so we can echo it back on the
  // cancel gate. Links sent before that existed won't have it, so it's always optional.
  // Rendered as plain text only -- this route is unauthenticated, so it must never reach ReactHtmlParser.
  const appointmentDate = new URLSearchParams(location.search).get('d')?.slice(0, APPOINTMENT_DATE_MAX_LENGTH) || '';

  // preflight the cancel link so an already-canceled or expired appointment shows its reason up
  // front rather than hiding it behind the confirmation button. The confirm path needs no preflight
  // because it acts immediately and surfaces the same message from the mutation itself.
  const {
    data: linkStatus,
    isFetching: isCheckingLink,
    isError: isLinkCheckError,
  } = useGetAppointmentLinkStatusQuery(
    { shortCode: params.shortCode, status: 'cancel' },
    { skip: !isCancel },
  );

  const handleCancelAppointment = () => {
    setAcknowlegement('You have successfully canceled your appointment. \n Please contact the office to reschedule.');
    updateAppointmentStatus({ shortCode: params.shortCode, status: 'cancel' });
  };

  useEffect(() => {
    // confirming is not destructive, so it still completes on load. Canceling waits for the patient
    // to confirm on the gate below.
    if (!isCancel) {
      setAcknowlegement('Your appointment is confirmed.');
      updateAppointmentStatus({ shortCode: params.shortCode, status: 'confirm' });
    }
  }, [location.pathname]);

  const isMutationSettling = data.status === 'uninitialized' || data.status === 'pending';
  // the confirm path fires on load and has no gate of its own, so show a loader until it settles
  const showLoader = (!isCancel && isMutationSettling) || (isCancel && isCheckingLink);
  // keep the gate mounted while the request is in flight so the button can show its loading state
  const showCancelGate = isCancel && !keptAppointment && linkStatus?.isActionable && isMutationSettling;

  // a blocked link reports its own reason; a failed mutation reports the server's
  let errorMessage = '';
  if (data.status === 'rejected') errorMessage = message;
  else if (isCancel && isLinkCheckError) errorMessage = LINK_CHECK_FAILED_MESSAGE;
  else if (isCancel && linkStatus && !linkStatus.isActionable) errorMessage = linkStatus.message;

  return (
    <div className="app-page">
      <div className="app-panels">
        <div className="list-panel__wrapper">
          <div className="empty-message__wrapper">
            <div className="empty-message__content">
              {showLoader && <LoaderCircle />}
              {showCancelGate && (
                <>
                  <h3>Cancel Appointment</h3>
                  <p className="u-text-large">
                    {appointmentDate
                      ? `You are about to cancel your appointment on ${appointmentDate}. Click confirm to finalize the cancellation.`
                      : 'You are about to cancel your appointment. Click confirm to finalize the cancellation.'}
                  </p>
                  <UtilityInlineGrid align="between">
                    <Button
                      type="input"
                      onClick={() => setKeptAppointment(true)}
                      disabled={data.isLoading}
                      data-cypress="keepAppointment"
                    >
                      Don&apos;t Cancel
                    </Button>
                    <Button
                      type="danger"
                      onClick={handleCancelAppointment}
                      loading={data.isLoading}
                      data-cypress="confirmCancelAppointment"
                    >
                      Confirm Cancellation
                    </Button>
                  </UtilityInlineGrid>
                </>
              )}
              {keptAppointment && (
                <>
                  <h3>No Changes Made</h3>
                  <p className="u-text-large">No changes were made to your appointment.</p>
                </>
              )}
              {errorMessage && (
                <>
                  <h3 style={{ color: 'red' }}>Unable to Update Appointment</h3>
                  <p className="u-text-large">{ReactHtmlParser(replaceNewLinesToLineBreaks(errorMessage))}</p>
                </>
              )}
              {data.status === 'fulfilled' && (
                <>
                  <h3>Thank You</h3>
                  <p className="u-text-large">{ReactHtmlParser(replaceNewLinesToLineBreaks(acknowlegement))}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentReminderAck;
